import React, { useEffect, useState } from 'react';

import { Info, Lightbulb } from 'lucide-react';

import AnswerContent from './components/AnswerContent';
import AnswerHeader from './components/AnswerHeader';
import AnswerSidebar from './components/AnswerSidebar';
import SupportingEvidence from './components/SupportingEvidence';
import { getConfidenceLevel, getTitleFromFlowType } from './helpers';
import FollowUpQuestions from '../Question/components/FollowUpQuestions';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import TooltipModal from '@/components/molecules/Modals/TooltipModal';
import SkeletonOrComponent from '@/components/molecules/SkeletonOrComponent';
import { cn } from '@/helpers/shadCnUtils';
import { Artifact, ExecuteKimSearch, Fact, KimMode, MessageType, ThreadMessage } from '@/types';

interface AnswerProps {
  threadId: string;
  caseId: string;
  messages: ThreadMessage[] | undefined;
  loading?: boolean;
  executeSearch: ExecuteKimSearch;
  createNewThread: () => void;
  goToThread: (threadId: string) => void;
  handleBack: () => void;
}

const Answer: React.FC<AnswerProps> = ({
  threadId,
  caseId,
  loading,
  messages,
  executeSearch,
  createNewThread,
  goToThread,
  handleBack,
}) => {
  const [facts, setFacts] = useState<Fact[]>([]);
  const [artifact, setArtifact] = useState<Artifact | undefined>();
  const [flowType, setFlowType] = useState<KimMode | undefined>();
  const [showAllChunks, setShowAllChunks] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [showConfidenceTooltip, setShowConfidenceTooltip] = useState(false);

  // Effects
  useEffect(() => {
    if (!loading && messages) {
      // Slight delay to make sure content is rendered
      setTimeout(() => setAnimate(true), 50);
    } else {
      setAnimate(false);
    }
  }, [loading, messages]);

  // If there are no facts but there are chunks, show the chunks
  useEffect(() => {
    if (!messages) return;
    const answerMessage = messages?.find((message) => message.type === MessageType.Assistant);
    if (!answerMessage) return;
    const { facts, chunks } = answerMessage;
    if (!facts || !chunks) return;
    if (facts.length === 0 && chunks.length > 0) {
      setShowAllChunks(true);
    }
  }, [messages]);

  useEffect(() => {
    if (!messages) return;
    const answerMessage = messages?.find((message) => message.type === MessageType.Assistant);
    if (!answerMessage) return;
    const { facts } = answerMessage;
    const flowType = answerMessage.config?.flowType;
    const artifacts = answerMessage.config?.artifacts;

    // Set artifact and flow_type if they exist
    setArtifact(undefined);
    setFlowType(flowType);

    if (artifacts && artifacts.length > 0) {
      setArtifact(artifacts[0]);
    }

    if (facts) {
      setFacts(facts);
    }
  }, [messages]);

  const userMessage = messages?.find((message) => message.type === 'user');
  const answerMessage = messages?.find((message) => message.type === 'assistant');
  const confidenceLevel = getConfidenceLevel(answerMessage?.config?.trafficLight);

  return (
    <div className="flex h-full">
      <AnswerSidebar
        userMessage={userMessage}
        createNewThread={createNewThread}
        goToThread={goToThread}
        handleBack={handleBack}
      />

      <div className="flex h-full w-full flex-1 flex-col overflow-y-auto px-4 py-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="mx-2 flex items-center gap-3">
            <Lightbulb className="text-brandSecondary mb-0.5 size-5" />

            <h2 className="text-base font-semibold text-gray-800">{getTitleFromFlowType(flowType)}</h2>

            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="ml-2 flex w-fit items-center gap-1.5 rounded-md border bg-gray-50 p-1.5 px-2.25 text-xs text-gray-500">
                    <Info size={12} />
                    <p className="-mb-0.25">Review sources to verify {getTitleFromFlowType(flowType).toLowerCase()}</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm bg-gray-50 p-2 px-3 text-gray-700" sideOffset={8}>
                  Kim has undergone extensive testing to increase the accuracy of the answers. However, as with all
                  generative AI systems, in complex scenarios it could return an inaccurate answer. Crucially, you
                  should check the source fact and document in the citation to verify the answer's completeness or
                  accuracy.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <AnswerHeader
            confidenceLevel={confidenceLevel}
            answerMessage={answerMessage}
            setShowConfidenceTooltip={setShowConfidenceTooltip}
            caseId={caseId}
            threadId={threadId}
            artifact={artifact}
            flowType={flowType}
          />
        </div>
        <SkeletonOrComponent
          isLoading={loading || !messages}
          component={<AnswerContent answerMessage={answerMessage} flowType={flowType} />}
        />
        {flowType === KimMode.ExtractionFlow || !flowType ? (
          <></>
        ) : (
          <>
            <SupportingEvidence
              loading={loading}
              animate={animate}
              answerMessage={answerMessage}
              facts={facts}
              showAllChunks={showAllChunks}
              setShowAllChunks={setShowAllChunks}
              confidenceLevel={confidenceLevel}
            />
            <FollowUpQuestions
              followUpQuestions={answerMessage?.config?.followUpQuestions}
              animate={animate}
              loading={loading}
              questionMode={flowType}
              executeSearch={executeSearch}
            />
          </>
        )}
      </div>

      <TooltipModal
        header={
          <div
            className={cn(
              'flex h-full w-full items-center justify-center rounded-t-lg',
              confidenceLevel.backgroundColor,
            )}
          ></div>
        }
        title={confidenceLevel.text}
        content={confidenceLevel.tooltip}
        subtext="All Kim answers have a confidence level, ranging from High to Low."
        isOpen={showConfidenceTooltip}
        handleClose={() => setShowConfidenceTooltip(false)}
      />
    </div>
  );
};

export default React.memo(Answer);
